// doppelteWege.ts — Erkennung „Diese Nummer/E-Mail-Adresse steht schon unter …"
// innerhalb EINES Kontakts (User 01.10.2026): Telefon/Mobil/Fax privat und
// geschäftlich, die weiteren Kommunikationswege und das Popup „Weiterer
// Kommunikationsweg".
//
// Rein (kein React), damit Formulare und Popup dieselbe Regel nutzen. Der
// Vergleich läuft über `wegVergleichsSchluessel` — derselbe Schlüssel, mit dem
// das Speichern Doppelungen erkennt (#1935/#1961): „0171 1234567" und
// „+49 171 1234567" sind dieselbe Nummer, E-Mail-Adressen ohne Groß/Klein.
// Nur Hinweis — gespeichert wird weiterhin alles, wie es dasteht.

import { wegVergleichsSchluessel } from "../../../utils/buildPrivateContactCreatePayload";
import type { WeitereKommunikationEintrag } from "../widgets/WeitereKommunikationDialog";

export type WegTyp = "phone" | "mobile" | "fax" | "email";

/** Ein befülltes (oder leeres) Kommunikationsfeld eines Kontakts. */
export interface KontaktWeg {
  /** Eindeutig je Feld: Formularfeld („phonePrivate") bzw. „extra-comm-<id>". */
  key: string;
  typ: WegTyp;
  wert: string;
  /** Wie das Feld in der Maske heißt („Telefon", „Mobil 2", „E-Mail"). */
  feldname: string;
  /** Spalte — fehlt bei der Firma (eine Spalte, alles geschäftlich). */
  zuordnung?: "privat" | "geschaeftlich";
}

/** Mindestens so viele Ziffern, bevor eine Nummer verglichen wird — halb getippt gibt es keinen Hinweis. */
const MIN_ZIFFERN = 7;

const istNummer = (typ: string): boolean => typ === "phone" || typ === "mobile" || typ === "fax";

/**
 * Vergleichsschlüssel eines Feldwerts, oder `null`, solange es nichts zu
 * vergleichen gibt (leer, halb getippte Nummer, E-Mail ohne „@").
 * Nummern und E-Mail-Adressen bekommen getrennte Präfixe — eine Nummer ist nie
 * „dieselbe" wie eine E-Mail-Adresse.
 */
export function wegSchluessel(typ: string, wert: string | null | undefined): string | null {
  const s = String(wert ?? "").trim();
  if (!s) return null;
  if (typ === "email") {
    const at = s.indexOf("@");
    if (at < 1 || at === s.length - 1) return null;
    return "e:" + s.toLowerCase();
  }
  if (!istNummer(typ)) return null;
  if (s.replace(/\D/g, "").length < MIN_ZIFFERN) return null;
  return "n:" + wegVergleichsSchluessel(s);
}

/**
 * Je Feld der ERSTE andere Weg desselben Kontakts mit demselben Wert. Beide
 * Felder einer Doppelung bekommen einen Hinweis (jeweils mit dem Namen des
 * anderen) — welches zuerst da war, weiß die Maske nicht.
 */
export function doppelteWege(wege: readonly KontaktWeg[]): Map<string, KontaktWeg> {
  const nachSchluessel = new Map<string, KontaktWeg[]>();
  for (const w of wege) {
    const k = wegSchluessel(w.typ, w.wert);
    if (!k) continue;
    const liste = nachSchluessel.get(k);
    if (liste) liste.push(w);
    else nachSchluessel.set(k, [w]);
  }
  const out = new Map<string, KontaktWeg>();
  for (const liste of nachSchluessel.values()) {
    if (liste.length < 2) continue;
    for (const w of liste) {
      const anderer = liste.find((x) => x.key !== w.key);
      if (anderer) out.set(w.key, anderer);
    }
  }
  return out;
}

/** Der vorhandene Weg mit demselben Wert wie (`typ`, `wert`) — für das Popup. */
export function vorhandenerWeg(
  typ: string,
  wert: string,
  vorhandene: readonly KontaktWeg[] | undefined,
): KontaktWeg | null {
  const k = wegSchluessel(typ, wert);
  if (!k || !vorhandene) return null;
  return vorhandene.find((w) => wegSchluessel(w.typ, w.wert) === k) ?? null;
}

/** „Diese Nummer steht schon unter Telefon (privat)." */
export function doppeltText(typ: string, vorbild: KontaktWeg): string {
  const was = typ === "email" ? "Diese E-Mail-Adresse" : "Diese Nummer";
  const spalte = vorbild.zuordnung
    ? ` (${vorbild.zuordnung === "geschaeftlich" ? "geschäftlich" : "privat"})`
    : "";
  return `${was} steht schon unter ${vorbild.feldname}${spalte}.`;
}

const GRUNDNAME: Record<WegTyp, string> = {
  phone: "Telefon",
  mobile: "Mobil",
  fax: "Fax",
  email: "E-Mail",
};

/**
 * Die weiteren Kommunikationswege mit denselben Feldnamen wie in der Maske:
 * „Telefon 2", „Mobil 3", „E-Mail 2"; Fax heißt in der Personenmaske „Fax"
 * bzw. „Fax 1/2 …" (kein Hauptfeld), in der Firmenmaske „Fax 2 …" (das
 * Hauptfeld heißt „Fax").
 *
 * `mitZuordnung`: Personenmaske (zwei Spalten; ohne Angabe = privat).
 */
export function weitereKommWege(
  extras: readonly WeitereKommunikationEintrag[],
  opts: { mitZuordnung: boolean; faxHauptfeld: boolean },
): KontaktWeg[] {
  const out: KontaktWeg[] = [];
  const spalten: Array<"privat" | "geschaeftlich" | undefined> = opts.mitZuordnung
    ? ["privat", "geschaeftlich"]
    : [undefined];
  for (const spalte of spalten) {
    const inSpalte = extras.filter((c) =>
      !opts.mitZuordnung ? true : spalte === "privat" ? !c.zuordnung || c.zuordnung === "privat" : c.zuordnung === "geschaeftlich",
    );
    for (const typ of ["phone", "mobile", "fax", "email"] as const) {
      const liste = inSpalte.filter((c) => c.type === typ);
      liste.forEach((c, idx) => {
        const feldname =
          typ === "fax" && !opts.faxHauptfeld
            ? liste.length > 1
              ? `Fax ${idx + 1}`
              : "Fax"
            : `${GRUNDNAME[typ]} ${idx + 2}`;
        out.push({ key: `extra-comm-${c.id}`, typ, wert: c.value, feldname, ...(spalte ? { zuordnung: spalte } : {}) });
      });
    }
  }
  return out;
}
